import { useState } from 'react';
import { Card, CardContent, Typography, Box, Button, Stack } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { TrendingUp } from 'lucide-react';
import type { ReviewTrendData } from '../../types/dashboard';

interface ReviewTrendsChartProps {
  data: ReviewTrendData[];
  hasReviews?: boolean;
}

const timePeriods = ['1D', '5D', '1M', '6M', '1Y', '5Y', 'Max'];

const ReviewTrendsChart = ({ data, hasReviews = true }: ReviewTrendsChartProps) => {
  const [selectedPeriod, setSelectedPeriod] = useState('1D');

  return (
    <Card
      sx={{
        background: '#FFFFFF',
        border: '1px solid #E4E4E7',
        borderRadius: '20px',
        boxShadow: 'none',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {!hasReviews && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.75) 0%, rgba(249, 250, 251, 0.85) 100%)',
            backdropFilter: 'blur(2px)',
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4,
            textAlign: 'center'
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              boxShadow: '0px 8px 24px rgba(79, 70, 229, 0.25)'
            }}
          >
            <TrendingUp size={32} color="#FFFFFF" />
          </Box>
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.9)',
              borderRadius: '12px',
              p: 3,
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              border: '1px solid rgba(79, 70, 229, 0.1)',
              maxWidth: '320px'
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 700,
                fontSize: '18px',
                lineHeight: '24px',
                color: '#111827',
                mb: 1
              }}
            >
              Analytics Awaiting Reviews! 📊
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Inter", sans-serif',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '20px',
                color: '#6B7280'
              }}
            >
              As soon as your customers share their feedback, we'll track trends and show you powerful analytics right here!
            </Typography>
          </Box>
        </Box>
      )}
      <CardContent sx={{ p: '24px', pb: '56px' }}>
        {/* Header with Title and Time Period Filters */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Typography
            sx={{
              fontFamily: '"Inter", sans-serif',
              fontWeight: 700,
              fontSize: '14px',
              lineHeight: '18px',
              color: '#2C2C2C'
            }}
          >
            Review Analytics
          </Typography>

          {/* Time Period Filters */}
          <Stack direction="row" spacing={0} divider={<Box sx={{ width: '1px', height: '20px', bgcolor: 'rgba(131, 131, 131, 0.5)' }} />}>
            {timePeriods.map((period) => (
              <Button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                sx={{
                  minWidth: 'auto',
                  px: 1.5,
                  py: 0.5,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontWeight: 600,
                  fontSize: selectedPeriod === period ? '14px' : '12px',
                  lineHeight: selectedPeriod === period ? '18px' : '15px',
                  color: selectedPeriod === period ? '#6425FE' : '#838383',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#6425FE'
                  }
                }}
              >
                {period}
              </Button>
            ))}
          </Stack>
        </Box>

        {/* Chart */}
        <Box sx={{ width: '100%', height: 298 }}>
          <ResponsiveContainer>
            <AreaChart 
              data={data}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="colorGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#823F7D" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#823F7D" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="0" 
                stroke="#F0F0F0" 
                horizontal={true}
                vertical={true}
                opacity={0.5}
              />
              <XAxis
                dataKey="week"
                tick={{ 
                  fill: '#838383', 
                  fontSize: 12,
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
                stroke="none"
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ 
                  fill: '#2C2C2C', 
                  fontSize: 12,
                  fontFamily: "'Poppins', sans-serif"
                }}
                stroke="none"
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#823F7D',
                  border: 'none',
                  borderRadius: 8,
                  padding: '12px',
                  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)'
                }}
                labelStyle={{
                  color: '#EAEAEA',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
                itemStyle={{
                  color: '#FFFFFF',
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: "'Plus Jakarta Sans', sans-serif"
                }}
                formatter={(value: number) => [`${value.toLocaleString()}`, '']}
              />
              <Area
                type="monotone"
                dataKey="count"
                stroke="#823F7D"
                strokeWidth={2}
                fill="url(#colorGradient)"
                dot={{ fill: '#823F7D', r: 4, strokeWidth: 0 }}
                activeDot={{ 
                  fill: '#823F7D', 
                  r: 6, 
                  stroke: '#FFFFFF',
                  strokeWidth: 3
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ReviewTrendsChart;